import React, { useState, useRef, useCallback, useEffect, useContext } from 'react';
import { streamChat, resolveChoice, submitChatFeedback, warmUpChat } from '../../API/chat';
import ChatMessage from './ChatMessage';
import ChatInput from './ChatInput';
import ChatToolIndicator from './ChatToolIndicator';
import ChatChoice from './ChatChoice';
import ChatLauncher from './ChatLauncher';
import ChatStarter from './ChatStarter';
import { ChatLoadingDots, ChatPoweredBy } from './ChatBrand';
import { useChatConfig, toPlainText } from './chatConfig';
import { CHAT_VIEWS, ChatNavMenu, ChatSearchHistory, ChatSearchTips, ChatSuggestedSearches, ChatTerms } from './ChatMenu';
import { AppContext } from '../../util/AppContext';
import { ChatThemeContext, getChatTheme } from './chatThemes';
import ChatIcon from './ChatIcon';
import closeIcon from '../../styles/assets/img/chat/close.svg';
import menuIcon from '../../styles/assets/img/chat/menu.svg';
import newChatIcon from '../../styles/assets/img/chat/nav-new-chat.svg';

const CHAT_VIEW = 'chat';

export default function ChatWidget({ standalone = false }) {
    const [appState] = useContext(AppContext);
    const chatConfig = useChatConfig();
    const theme = getChatTheme(chatConfig.Theme);
    const themeClass = theme.className ? ` ${theme.className}` : '';

    // Only show the chat assistant after the user has authenticated
    // and the application has fully loaded.
    const isAuthenticated = appState.user !== undefined && appState.user !== '';
    const isAppReady = !!appState.appReady;

    const [isOpen, setIsOpen] = useState(false);
    const [startWithVoice, setStartWithVoice] = useState(false);
    const [messages, setMessages] = useState([]); // { id, role, content }
    const [inputValue, setInputValue] = useState('');
    const [isStreaming, setIsStreaming] = useState(false);
    const [isThinking, setIsThinking] = useState(false);
    const [toolIndicators, setToolIndicators] = useState([]); // { id, toolName, completed }
    const [error, setError] = useState(null);
    const [pendingChoice, setPendingChoice] = useState(null); // { choiceId, question, options, pageSize }
    const [voiceReply, setVoiceReply] = useState(null);
    const [view, setView] = useState(CHAT_VIEW); // CHAT_VIEW or one of CHAT_VIEWS

    const messagesEndRef = useRef(null);
    const cancelRef = useRef(null);
    const assistantBufferRef = useRef('');
    const spokenBufferRef = useRef('');
    const msgIdRef = useRef(0);
    const messagesRef = useRef(messages);
    messagesRef.current = messages;

    const nextId = () => ++msgIdRef.current;

    // Auto-scroll to bottom on new messages
    const scrollToBottom = useCallback(() => {
        requestAnimationFrame(() => {
            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        });
    }, []);

    useEffect(() => {
        scrollToBottom();
    }, [messages, toolIndicators, isThinking, pendingChoice, view, scrollToBottom]);

    // Cancel any in-flight stream when unmounting
    useEffect(() => {
        return () => cancelRef.current?.cancel();
    }, []);

    // Pre-warm the backend prompt cache as soon as the user is authenticated and the
    // app is ready — before they type anything. The warmup sends the system prompt and
    // tool schemas to the LLM so the first real question skips the expensive prefill.
    const warmedRef = useRef(false);
    useEffect(() => {
        if (!isAuthenticated || !isAppReady || warmedRef.current) return;
        warmedRef.current = true;
        warmUpChat().catch(() => {});
    }, [isAuthenticated, isAppReady]);

    const handleSend = useCallback((overrideText) => {
        const text = (overrideText !== undefined ? overrideText : inputValue).trim();
        // Block sending only if there's no text, or if streaming AND no pending choice
        if (!text || (isStreaming && !pendingChoice)) return;

        // If there's a pending choice, resolve it with the typed text instead of starting a new stream
        if (pendingChoice) {
            const resolvedChoiceId = pendingChoice.choiceId;
            setError(null);
            setInputValue('');

            // Retain the question and the typed answer in the transcript so the
            // conversation history still makes sense once the choice card clears.
            const questionMsg = { id: nextId(), role: 'assistant', content: pendingChoice.question };
            const userMsg = { id: nextId(), role: 'user', content: text };
            setMessages((prev) => [...prev, questionMsg, userMsg]);

            // Resolve the choice with the free-text response
            resolveChoice(resolvedChoiceId, text)
                .then(() => {
                    setPendingChoice((prev) =>
                        prev && prev.choiceId === resolvedChoiceId ? null : prev
                    );
                })
                .catch(() => {
                    setError('Failed to send response. Please try again.');
                });
            return;
        }

        setError(null);
        setInputValue('');

        // Add user message
        const userMsg = { id: nextId(), role: 'user', content: text };
        setMessages((prev) => [...prev, userMsg]);

        // Build conversation history for the API (use ref to always get latest messages)
        const apiMessages = [
            ...messagesRef.current.map((m) => ({ role: m.role, content: m.content })),
            { role: 'user', content: text },
        ];

        // Start streaming
        setIsStreaming(true);
        assistantBufferRef.current = '';
        spokenBufferRef.current = '';

        let assistantId = nextId();
        const updateAssistantMessage = (update) => {
            const messageId = assistantId;
            setMessages((prev) => prev.some((message) => message.id === messageId)
                ? prev.map((message) => message.id === messageId ? update(message) : message)
                : [...prev, update({ id: messageId, role: 'assistant', content: '' })]);
        };

        cancelRef.current = streamChat(apiMessages, {
            onToken: (token) => {
                setIsThinking(false);
                assistantBufferRef.current += token;
                const snapshot = assistantBufferRef.current;
                updateAssistantMessage((message) => ({ ...message, content: snapshot }));
            },
            onSpeech: (text) => {
                spokenBufferRef.current += text;
            },
            onThinking: () => {
                setIsThinking(true);
            },
            onToolCall: (toolName) => {
                setIsThinking(false);
                const toolId = nextId();
                setToolIndicators((prev) => [...prev, { id: toolId, toolName, completed: false }]);
            },
            onToolResult: (toolName, toolResultData) => {
                setToolIndicators((prev) =>
                    prev.map((t, i) =>
                        i === prev.length - 1 && t.toolName === toolName && !t.completed
                            ? { ...t, completed: true }
                            : t
                    )
                );
                // Remove completed tool indicators after a brief delay
                setTimeout(() => {
                    setToolIndicators((prev) => prev.filter((t) => !t.completed));
                }, 2000);

                // If a sell sheet was generated, attach the sell sheet data to the latest assistant message
                if (toolResultData && (toolName === 'generate_sell_sheet' || toolName === 'generate_sell_sheet_smart')) {
                    try {
                        const parsed = JSON.parse(toolResultData);
                        if (parsed.success && (parsed.sellSheetId != null || parsed.pdfBase64)) {
                            updateAssistantMessage((message) => ({
                                ...message,
                                sellSheet: { id: parsed.sellSheetId, fileName: parsed.fileName, pdfBase64: parsed.pdfBase64 },
                            }));
                        }
                    } catch (e) {
                        console.error('[ChatWidget] Error parsing sell sheet result:', e);
                    }
                }
            },
            onChoice: (choice) => {
                assistantId = nextId();
                assistantBufferRef.current = '';
                spokenBufferRef.current = '';
                setPendingChoice(choice);
                setVoiceReply({ id: nextId(), text: choice.question });
            },
            onError: (err) => {
                setError(err);
                setVoiceReply({ id: nextId(), text: err });
                setIsThinking(false);
                // If assistant message is empty, remove it
                setMessages((prev) =>
                    prev.filter((m) => !(m.id === assistantId && !m.content))
                );
            },
            onDone: () => {
                setIsStreaming(false);
                setIsThinking(false);
                setVoiceReply({ id: nextId(), text: spokenBufferRef.current || assistantBufferRef.current });
                cancelRef.current = null;
                // If assistant message is still empty, show a fallback
                updateAssistantMessage((message) => message.content || message.sellSheet
                    ? message
                    : { ...message, content: 'I apologize, but I wasn\'t able to generate a response. Please try again.' });
            },
        });
    }, [inputValue, isStreaming, pendingChoice]);

    // Ref so handleSuggestion and ChatInput can always call the latest handleSend (avoids stale state)
    const handleSendRef = useRef(handleSend);
    handleSendRef.current = handleSend;

    // Stable wrapper for ChatInput — always delegates to the latest handleSend via ref
    const stableOnSend = useCallback((overrideText) => {
        handleSendRef.current(overrideText);
    }, []);

    const handleSuggestion = useCallback((rawSuggestion) => {
        const suggestion = toPlainText(rawSuggestion);
        setInputValue(suggestion);
        // Auto-submit the suggestion to the AI agent
        setTimeout(() => {
            const ta = document.querySelector('.chat-widget-textarea');
            ta?.focus();
            // Trigger send directly since setInputValue is async
            handleSendRef.current(suggestion);
        }, 0);
    }, []);

    const handleChoiceSelect = useCallback(async (selectedId) => {
        if (!pendingChoice) return;
        const resolvedChoiceId = pendingChoice.choiceId;

        // Find the label for the selected option to show in chat
        const selectedOption = pendingChoice.options?.find(o => o.id === selectedId);
        const selectedLabel = selectedOption?.label || selectedId;

        // Retain the question and the selected answer in the transcript so the
        // conversation history still makes sense once the choice card clears.
        const questionMsg = { id: nextId(), role: 'assistant', content: pendingChoice.question };
        const userMsg = { id: nextId(), role: 'user', content: selectedLabel };
        setMessages((prev) => [...prev, questionMsg, userMsg]);

        try {
            await resolveChoice(resolvedChoiceId, selectedId);
        } catch (err) {
            setError('Failed to send selection. Please try again.');
        }
        setPendingChoice((prev) =>
            prev && prev.choiceId === resolvedChoiceId ? null : prev
        );
    }, [pendingChoice]);
    const handleFeedback = useCallback(async (messageId, rating) => {
        const current = messagesRef.current;
        const index = current.findIndex((m) => m.id === messageId);
        if (index < 0) return;
        const previousRating = current[index].feedback;
        if (previousRating === rating) return;

        // The question is the closest user message before this answer
        const question = current.slice(0, index).reverse().find((m) => m.role === 'user')?.content || '';
        const setRating = (value) => setMessages((prev) =>
            prev.map((m) => (m.id === messageId ? { ...m, feedback: value } : m))
        );

        setRating(rating);
        try {
            await submitChatFeedback({ rating, question, answer: current[index].content });
        } catch (err) {
            setRating(previousRating);
            setError('Failed to send feedback. Please try again.');
        }
    }, []);

    const resetChat = useCallback(() => {
        cancelRef.current?.cancel();
        cancelRef.current = null;
        setMessages([]);
        setInputValue('');
        setIsStreaming(false);
        setIsThinking(false);
        setToolIndicators([]);
        setPendingChoice(null);
        setError(null);
    }, []);

    // Ask a question picked from a menu screen (Search History, Suggested Searches)
    const handleAskFromMenu = useCallback((question) => {
        setView(CHAT_VIEW);
        handleSuggestion(question);
    }, [handleSuggestion]);

    const handleNewChat = useCallback(() => {
        resetChat();
        setView(CHAT_VIEW);
    }, [resetChat]);

    // Home returns to the initial chat screen (intro card + suggested questions)
    const handleHome = useCallback(() => {
        resetChat();
        setView(CHAT_VIEW);
    }, [resetChat]);

    const toggleMenu = useCallback(() => {
        // The input remounts when the chat view comes back; don't restart the launcher's voice input
        setStartWithVoice(false);
        setView((prev) => (prev === CHAT_VIEW ? CHAT_VIEWS.menu : CHAT_VIEW));
    }, []);

    const openChat = useCallback((withVoice) => {
        setStartWithVoice(withVoice);
        setIsOpen(true);
    }, []);

    const closeChat = useCallback(() => {
        setIsOpen(false);
        setStartWithVoice(false);
    }, []);

    // Escape closes the floating panel
    useEffect(() => {
        if (!isOpen || standalone) return undefined;
        const onKeyDown = (e) => {
            if (e.key === 'Escape') closeChat();
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [isOpen, standalone, closeChat]);

    // Hide the chat widget entirely from unauthenticated users
    if (!isAuthenticated || !isAppReady) {
        return standalone ? (
            <main className={`chat-standalone-message${themeClass}`} aria-busy="true">
                Loading {chatConfig.AssistantName}…
            </main>
        ) : null;
    }

    if (!chatConfig.enabled) {
        return standalone ? (
            <main className={`chat-standalone-message${themeClass}`}>
                {chatConfig.AssistantName} is not available.
            </main>
        ) : null;
    }

    if (!standalone && !isOpen) {
        return (
            <ChatThemeContext.Provider value={theme}>
                <ChatLauncher
                    className={theme.className}
                    title={chatConfig.LauncherTitle}
                    onOpen={() => openChat(false)}
                    onOpenWithVoice={() => openChat(true)}
                />
            </ChatThemeContext.Provider>
        );
    }

    // The streaming assistant message stays empty until the first token; show the loading dots instead
    const visibleMessages = messages.filter((m) => m.role === 'user' || m.content || m.sellSheet);
    const lastMessageId = messages[messages.length - 1]?.id;
    const isWaitingForReply = isStreaming && toolIndicators.length === 0 && !pendingChoice
        && (isThinking || messages[messages.length - 1]?.role === 'user' || messages[messages.length - 1]?.content === '');

    return (
        <ChatThemeContext.Provider value={theme}>
            <div
                className={`chat-panel${standalone ? ' chat-panel-standalone' : ''}${themeClass}`}
                role={standalone ? 'main' : 'dialog'}
                aria-label={chatConfig.AssistantName}
            >
                <div className="chat-panel-header">
                    <img className="chat-panel-logo" src={chatConfig.LogoUrl} alt={chatConfig.LogoAlt} />
                    <div className="chat-panel-header-actions">
                        {!standalone && (
                            <button type="button" className="chat-panel-icon-btn" onClick={closeChat} aria-label="Close chat">
                                <ChatIcon src={closeIcon} className="chat-panel-icon" />
                            </button>
                        )}
                        <div className="chat-panel-nav-icons">
                            <button type="button" className="chat-panel-icon-btn" onClick={handleNewChat} aria-label="New chat" title="New chat">
                                <ChatIcon src={newChatIcon} className="chat-panel-icon" />
                            </button>
                            <button
                                type="button"
                                className={`chat-panel-icon-btn chat-panel-menu-btn${view !== CHAT_VIEW ? ' chat-panel-menu-btn-active' : ''}`}
                                onClick={toggleMenu}
                                aria-label={view === CHAT_VIEW ? 'Open menu' : 'Back to chat'}
                                aria-expanded={view !== CHAT_VIEW}
                            >
                                <ChatIcon src={menuIcon} className="chat-panel-icon chat-panel-menu-icon" />
                            </button>
                        </div>
                    </div>
                </div>
    
                {view !== CHAT_VIEW && (
                    <div className="chat-panel-body chat-panel-screen">
                        {view === CHAT_VIEWS.menu && (
                            <ChatNavMenu onNavigate={setView} onNewChat={handleNewChat} onHome={handleHome} />
                        )}
                        {view === CHAT_VIEWS.terms && (
                            <ChatTerms content={chatConfig.TermsContent} onBack={() => setView(CHAT_VIEWS.menu)} />
                        )}
                        {view === CHAT_VIEWS.tips && (
                            <ChatSearchTips
                                onBack={() => setView(CHAT_VIEWS.menu)}
                                onTrySuggested={() => setView(CHAT_VIEWS.suggested)}
                            />
                        )}
                        {view === CHAT_VIEWS.suggested && (
                            <ChatSuggestedSearches
                                suggestions={chatConfig.Suggestions}
                                onBack={() => setView(CHAT_VIEWS.tips)}
                                onAsk={handleAskFromMenu}
                            />
                        )}
                        {view === CHAT_VIEWS.history && (
                            <ChatSearchHistory onBack={() => setView(CHAT_VIEWS.menu)} onAsk={handleAskFromMenu} />
                        )}
                    </div>
                )}
    
                {view === CHAT_VIEW && (
                    <>
                        <div className="chat-panel-body">
                            <div className="chat-panel-messages">
                                {messages.length === 0 && (
                                    <ChatStarter
                                        introTitle={chatConfig.IntroTitle}
                                        introMessage={chatConfig.IntroMessage}
                                        suggestionsTitle={chatConfig.SuggestionsTitle}
                                        suggestions={chatConfig.Suggestions}
                                        onSuggestion={handleSuggestion}
                                    />
                                )}
            
                                {visibleMessages.map((msg) => (
                                    <ChatMessage
                                        key={msg.id}
                                        message={msg}
                                        // Feedback (thumbs) temporarily hidden per product decision;
                                        // handleFeedback stays wired below for when it returns.
                                        onFeedback={undefined}
                                    />
                                ))}
            
                                {toolIndicators.map((t) => (
                                    <ChatToolIndicator
                                        key={t.id}
                                        toolName={t.toolName}
                                        completed={t.completed}
                                    />
                                ))}
            
                                {/* Choice prompt — shown when the AI needs user input */}
                                {pendingChoice && (
                                    <ChatChoice
                                        key={pendingChoice.choiceId}
                                        choiceId={pendingChoice.choiceId}
                                        question={pendingChoice.question}
                                        options={pendingChoice.options}
                                        pageSize={pendingChoice.pageSize || 4}
                                        onSelect={handleChoiceSelect}
                                    />
                                )}
            
                                {isWaitingForReply && <ChatLoadingDots />}
            
                                {error && (
                                    <div className="chat-error-banner" role="alert">
                                        {error}
                                    </div>
                                )}
            
                                <div ref={messagesEndRef} />
                            </div>
                        </div>
            
                        <div className="chat-panel-footer">
                            <ChatInput
                                value={inputValue}
                                onChange={setInputValue}
                                onSend={stableOnSend}
                                voiceReply={voiceReply}
                                startListeningOnMount={startWithVoice}
                                disabled={isStreaming && !pendingChoice}
                                placeholder={
                                    pendingChoice
                                        ? 'Choose an option above or type your own response'
                                        : isStreaming
                                            ? `${chatConfig.AssistantName} is responding…`
                                            : chatConfig.InputPlaceholder
                                }
                            />
                            <div className="chat-disclaimer">
                                {chatConfig.Disclaimer.map((line) => (
                                    <p key={line}>{line}</p>
                                ))}
                                <ChatPoweredBy />
                            </div>
                        </div>
                    </>
                )}
            </div>
        </ChatThemeContext.Provider>
    );
}
